import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { MedicineResponseDto } from "libs/cellera-sdk";
import { useCallback, useEffect, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";
import { MedicineInPrescription } from "types/medicine";

import ConfirmDeleteModal from "./ConfirmDeleteModal";
import EmptyMedicine from "./EmptyMedicine";
import MedicineCreator from "./MedicineCreator";

const TABLE_HEADERS = [
  {
    label: "Product Name",
    key: "name",
  },
  {
    label: "Dosage",
    key: "dosage",
  },
  {
    label: "Qty",
    key: "quantity",
  },
  {
    label: "Unit",
    key: "unit",
  },
  {
    label: "Price",
    key: "price",
  },
  {
    label: "Actions",
    key: "actions",
    width: "100px",
  },
];

interface MedicineProps {
  prescriptionId?: string;
  isSaving?: boolean;
}

function Medicine({ prescriptionId }: MedicineProps) {
  const { colors } = useCelleraTheme();
  const [showMedicineCreator, setShowMedicineCreator] = useState(false);
  const [medicineToDelete, setMedicineToDelete] =
    useState<MedicineInPrescription | null>(null);
  const [currentPrescriptionId, setCurrentPrescriptionId] = useState<
    string | undefined
  >(prescriptionId);

  // Update currentPrescriptionId when prescriptionId prop changes
  useEffect(() => {
    if (prescriptionId) {
      setCurrentPrescriptionId(prescriptionId);
    }
  }, [prescriptionId]);

  const handleAddNewMedicine = useCallback(() => {
    setShowMedicineCreator(true);
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    }, 100);
  }, []);

  const handleCloseMedicineCreator = useCallback(() => {
    setShowMedicineCreator(false);
  }, []);

  const handleConfirmMedicines = useCallback(
    async (
      selectedMedicines: Array<{
        medicine: MedicineResponseDto;
        quantity: number;
      }>
    ) => {
      // TODO: Implement add medicines to prescription
      console.log("handleConfirmMedicines called with:", selectedMedicines);
      console.log("Current prescription ID:", currentPrescriptionId);

      // For now, just log the data that would be sent to API
      setShowMedicineCreator(false);
    },
    [currentPrescriptionId]
  );

  const handleCloseDeleteModal = useCallback(() => {
    setMedicineToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!medicineToDelete) return;

    // TODO: Implement remove medicine from prescription
    console.log("handleConfirmDelete called for medicine:", medicineToDelete);
    console.log("Current prescription ID:", currentPrescriptionId);

    handleCloseDeleteModal();
  }, [medicineToDelete, currentPrescriptionId, handleCloseDeleteModal]);

  return (
    <Box sx={{ width: "100%" }} className="space-y-5">
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${colors.border.main}`,
          borderRadius: 1,
          overflow: "hidden",
          maxHeight: "550px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableContainer>
          <Table stickyHeader sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: colors.background.secondary,
                }}
              >
                {TABLE_HEADERS.map((header) => (
                  <TableCell
                    key={header.key}
                    sx={{
                      fontWeight: 600,
                      py: 1.5,
                      borderRight: `1px solid ${colors.border.main}`,
                      backgroundColor: colors.background.secondary,
                      width: header.width,
                    }}
                    align={header.key === "actions" ? "center" : "left"}
                  >
                    {header.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <EmptyMedicine onCreateMedicine={handleAddNewMedicine} />
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {showMedicineCreator && (
        <MedicineCreator
          onClose={handleCloseMedicineCreator}
          isAdding={false}
          onConfirm={handleConfirmMedicines}
        />
      )}

      <ConfirmDeleteModal
        open={!!medicineToDelete}
        medicineName={medicineToDelete?.name || ""}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        isDeleting={false}
      />
    </Box>
  );
}

export default Medicine;
